import { AlertTriangle, ShieldAlert } from "lucide-react";
import { useQuery } from "convex/react";
import { api as convexApi } from "../../../lib/convexApi";

// Standing health strip for the Security Room.
//
// Every page in here reports counts, and a count of zero is ambiguous: it can
// mean "nothing bad happened" or "this control is not reporting". The audit
// found the second case several times over — host writes rejected and swallowed
// into a console.warn, enforcement parked in observe mode with no cue on the
// pages showing verdicts — and in each case the product presented the failure
// as good news.
//
// This strip renders only when something is actually wrong, so it stays quiet
// in the healthy case and is impossible to miss otherwise.
export function ControlPlaneBanner() {
  const health = useQuery((convexApi as any).security_room.health.controlPlane, {}) as
    | {
        warnings: Array<{ code: string; severity: "warn" | "critical"; message: string }>;
        hostTelemetryAttestable?: boolean;
        hostAttestationApplicable?: boolean;
      }
    | undefined;

  const warnings = health?.warnings ?? [];
  // The telemetry signal is only proof when host writes can be attested. With
  // no shared secret configured — the shipping desktop case — any signed-in
  // member can write a `terminal.*` row from a browser console, which silences
  // the staleness warning for 24h. Review demonstrated exactly that. A strip
  // that exists to distinguish "quiet" from "broken" must not let a forgeable
  // green read as an attested one, so it says which kind of quiet this is.
  const unattestable =
    health !== undefined &&
    health.hostTelemetryAttestable === false &&
    // Not applicable on a self-host deployment: there is no host/client split
    // to attest, so the advice below could never clear the strip.
    health.hostAttestationApplicable !== false;
  const telemetryWarning = warnings.some(
    (w) =>
      w.code === "no-host-telemetry" ||
      w.code === "stale-host-telemetry" ||
      w.code === "unattested-host-telemetry"
  );
  const showUnattestableNote = unattestable && !telemetryWarning;

  if (warnings.length === 0 && !showUnattestableNote) return null;

  return (
    <div className="sr-health" role="status" aria-live="polite">
      {warnings.map((w) => (
        <div key={w.code} className={`sr-health__item is-${w.severity}`}>
          {w.severity === "critical" ? (
            <ShieldAlert size={14} aria-hidden="true" />
          ) : (
            <AlertTriangle size={14} aria-hidden="true" />
          )}
          <span>{w.message}</span>
        </div>
      ))}
      {showUnattestableNote && (
        <div className="sr-health__item is-warn">
          <AlertTriangle size={14} aria-hidden="true" />
          <span>
            Terminal and egress telemetry is arriving, but cannot be attested — no host secret is
            configured, so these rows are vouched for by the signed-in client rather than proven to
            come from the host. Set AGENTGUARD_HOST_AUDIT_SECRET on the Convex deployment and the
            host to make this signal trustworthy.
          </span>
        </div>
      )}
    </div>
  );
}
